import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import { Box } from '@mui/material';
import { purple } from '@mui/material/colors';
import * as d3 from 'd3';

type NodeRec = {
  id: string;
  label: string;
  type: string;
  img?: string;
};

type LinkRec = {
  sid: string;
  tid: string;
  rel: string;
};

const s: any = {
  paper: {
    backgroundColor: 'white',
  },
  header: {
    fontSize: 24,
    fontWeight: 'typography.fontWeightLight',
    color: 'white',
    backgroundColor: purple[600],
    padding: 5,
    marginTop:10
  },
  body: {
    padding: '8px 12px 12px 12px',
  },
  stage: {
    width: '100%',
    height: 540,
    border: '1px solid #ececec',
    borderRadius: 8,
    background: 'linear-gradient(180deg, #fcfcff 0%, #f7f8fb 100%)',
  },
  legend: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 8,
    color: '#455a64',
    fontSize: 12,
  },
};

class RelationshipGraph extends Component<any, any> {
  static propTypes: any;
  static defaultProps: any;
  private containerRef: HTMLDivElement | null = null;
  private svgRef: SVGSVGElement | null = null;
  private simulation: any = null;
  private resolveD3Event = (evt?: any) => evt || (d3 as any).event || null;

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.data !== this.props.data) this.draw();
  }

  componentWillUnmount() {
    if (this.simulation) this.simulation.stop();
  }

  getNodeType = (id: string) => {
    if (id.indexOf('m_') === 0) return 'member';
    if (id.indexOf('g_') === 0) return 'group';
    if (id.indexOf('dt_') === 0) return 'dues';
    return 'unknown';
  };

  normalizeGraph = () => {
    const payload = this.props.data || {};
    const graph = payload.graph || {};
    let nodesRaw: any[] = Array.isArray(graph.nodes) ? graph.nodes : [];
    let linksRaw: any[] = Array.isArray(graph.links) ? graph.links : [];

    if (nodesRaw.length === 0 && linksRaw.length === 0) {
      const recent = Array.isArray(payload.recent) ? payload.recent : [];
      const daily = Array.isArray(payload.daily) ? payload.daily : [];
      const monthly = Array.isArray(payload.monthly) ? payload.monthly : [];
      nodesRaw = recent.map((r: any) => ({
        id: `m_${r.rid}`,
        label: r.nam || `Member ${r.rid}`,
        type: 'member',
        img: r.img || '',
      }));
      linksRaw = daily.concat(monthly).map((l: any) => ({
        sid: String(l.sid || l.source || ''),
        tid: String(l.tid || l.target || ''),
        rel: String(l.rel || 'relation'),
      }));
    }

    const nodeMap: { [key: string]: NodeRec } = {};
    for (let i = 0; i < nodesRaw.length; i += 1) {
      const n = nodesRaw[i] || {};
      const id = String(n.id || n.sid || n.tid || '');
      if (!id) continue;
      nodeMap[id] = {
        id,
        label: String(n.label || n.nam || n.name || id),
        type: String(n.type || this.getNodeType(id)),
        img: n.img || undefined,
      };
    }

    const linkMap: { [key: string]: LinkRec } = {};
    for (let i = 0; i < linksRaw.length; i += 1) {
      const l = linksRaw[i] || {};
      const sid = String(l.sid || l.source || '');
      const tid = String(l.tid || l.target || '');
      if (!sid || !tid) continue;
      const rel = String(l.rel || 'relation');
      const key = `${sid}|${tid}|${rel}`;
      linkMap[key] = { sid, tid, rel };

      if (!nodeMap[sid]) nodeMap[sid] = { id: sid, label: sid, type: this.getNodeType(sid) };
      if (!nodeMap[tid]) nodeMap[tid] = { id: tid, label: tid, type: this.getNodeType(tid) };
    }

    const nodes = Object.keys(nodeMap).map((k) => nodeMap[k]);
    const links = Object.keys(linkMap).map((k) => linkMap[k]);
    return { nodes, links };
  };

  draw = () => {
    if (!this.containerRef || !this.svgRef) return;
    const { nodes, links } = this.normalizeGraph();

    const width = Math.max(this.containerRef.clientWidth || 900, 320);
    const height = this.props.height || 540;
    const svg = d3.select(this.svgRef);
    svg.selectAll('*').remove();

    if (this.simulation) this.simulation.stop();
    if (nodes.length === 0) return;

    svg.attr('viewBox', `0 0 ${width} ${height}`);
    const root = svg.append('g');
    svg.call(
      d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.5, 2.5])
        .on('zoom', (ev: any) => {
          const zevent: any = this.resolveD3Event(ev);
          if (!zevent || !zevent.transform) return;
          root.attr('transform', zevent.transform);
        })
    );

    const linkDistance = (l: any) => {
      if (l.rel === 'spouse') return 45;
      if (l.rel === 'parent') return 95;
      if (l.rel === 'group') return 110;
      if (l.rel === 'dues_type' || l.rel === 'group_dues_type') return 135;
      return 90;
    };

    const linkColor = (rel: string) => {
      if (rel === 'spouse') return '#ef5350';
      if (rel === 'parent') return '#1e88e5';
      if (rel === 'group') return '#43a047';
      if (rel === 'dues_type' || rel === 'group_dues_type') return '#fb8c00';
      return '#90a4ae';
    };

    const nodeColor = (type: string) => {
      if (type === 'member') return '#5e35b1';
      if (type === 'group') return '#00897b';
      if (type === 'dues') return '#f57c00';
      return '#546e7a';
    };

    const nodeRadius = (type: string) => {
      if (type === 'group') return 10;
      if (type === 'dues') return 8;
      return 7;
    };

    const simNodes: any[] = nodes.map((n) => ({ ...n }));
    const simLinks: any[] = links.map((l) => ({ ...l, source: l.sid, target: l.tid }));

    const link = root
      .append('g')
      .attr('stroke-opacity', 0.5)
      .selectAll('line')
      .data(simLinks)
      .join('line')
      .attr('stroke-width', 1.6)
      .attr('stroke', (d: any) => linkColor(d.rel))
      .attr('stroke-dasharray', (d: any) => (d.rel === 'group' || d.rel === 'group_dues_type' ? '4,3' : '0'));

    link.append('title').text((d: any) => d.rel);

    const node = root
      .append('g')
      .selectAll('circle')
      .data(simNodes)
      .join('circle')
      .attr('r', (d: any) => nodeRadius(d.type))
      .attr('fill', (d: any) => nodeColor(d.type))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    node.append('title').text((d: any) => `${d.label} (${d.type})`);

    const labels = root
      .append('g')
      .attr('font-size', 10)
      .attr('fill', '#263238')
      .selectAll('text')
      .data(simNodes)
      .join('text')
      .text((d: any) => d.label)
      .attr('dx', 9)
      .attr('dy', 3)
      .attr('opacity', (d: any) => (d.type === 'member' ? 1 : 0.9));

    const dragstarted = (event: any, d: any) => {
      if (!event.active) this.simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    };
    const dragged = (event: any, d: any) => {
      d.fx = event.x;
      d.fy = event.y;
    };
    const dragended = (event: any, d: any) => {
      if (!event.active) this.simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    };

    node.call(d3.drag<SVGCircleElement, any>().on('start', dragstarted).on('drag', dragged).on('end', dragended) as any);

    this.simulation = d3
      .forceSimulation(simNodes as any)
      .force(
        'link',
        d3
          .forceLink(simLinks as any)
          .id((d: any) => d.id)
          .distance((d: any) => linkDistance(d))
      )
      .force('charge', d3.forceManyBody().strength(-190))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collide', d3.forceCollide().radius((d: any) => nodeRadius(d.type) + 4))
      .on('tick', () => {
        link
          .attr('x1', (d: any) => d.source.x)
          .attr('y1', (d: any) => d.source.y)
          .attr('x2', (d: any) => d.target.x)
          .attr('y2', (d: any) => d.target.y);

        node.attr('cx', (d: any) => d.x).attr('cy', (d: any) => d.y);
        labels.attr('x', (d: any) => d.x).attr('y', (d: any) => d.y);
      });
  };

  render() {
    const { title } = this.props;
    return (
      <Paper sx={{ ...s.paper }}>
        <Box sx={{ ...s.header }}>{title || 'Member Relationship Graph'}</Box>
        <Box sx={{ ...s.body }}>
          <Box sx={{ ...s.legend }}>
            <span>Member: Purple</span>
            <span>Group: Teal</span>
            <span>Dues Type: Orange</span>
            <span>Parent: Blue link</span>
            <span>Spouse: Red link</span>
            <span>Group/Dues: Dashed link</span>
          </Box>
          <Box ref={(n: any) => (this.containerRef = n as HTMLDivElement | null)} sx={{ ...s.stage }}>
            <svg ref={(n: any) => (this.svgRef = n as SVGSVGElement | null)} width="100%" height="100%" />
          </Box>
        </Box>
      </Paper>
    );
  }
}

RelationshipGraph.propTypes = {
  title: PropTypes.string,
  data: PropTypes.object,
  height: PropTypes.number,
};

export default RelationshipGraph;
